import { App } from '../../App';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import { Button, Card, Checkbox, Link, Modal, Text } from 'components';
import { dateFormat, numberToCurrency } from 'utils/translate';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getFetchHeaders } from '../../utils/fetchHeaders';
import { getScreenTranslation } from '../../utils/translations';
import { injectJuicyScript } from '../../utils/injectScript';
import { ioBlackBoxAtom } from '../../hooks/useIovation';
import { routes } from '../../routes';
import { useAnalyticsCookies } from '../../hooks/useAnalyticsCookies';
import { useErrorManager } from 'InternalLibrary';
import {
  useGetClientApplication,
  useGetClientApplicationProposals,
  useGetLegalTerms,
  usePostClientApplication,
  usePutApplication,
} from '../../api';
import { useJuicy } from '../../hooks/useJuicy';
import { useNavigate } from 'react-router-dom';
import useClient from '../../hooks/useClient';
import useClientOffer from '../../hooks/useClientOffer';

import { getApplicationSource } from '../../utils/isInWebView';
import { postMessageToApp } from '../../utils/postMessageToApp';
import { useRecoilValue } from 'recoil';
import { useSeon } from '../../hooks/useSeon';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import layout from 'scss/variables.module.scss';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import styles from './BestOffer.module.scss';

export default function BestOffer() {
  const i18Next = useTranslation('bestOffer');
  const { client, fetchClient } = useClient();
  const [consents, setConsents] = useState(false);
  const [isLegalTermsOpen, setIsLegalTermsOpen] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [isLoadingLegalTerms, setIsLoadingLegalTerms] = useState(false);
  const [legalTermsHtml, setLegalTermsHtml] = useState<string>(); // Holds html

  const apiLegalTerms = useGetLegalTerms();
  const { data: offerData, fetch: fetchOffer } = useClientOffer();
  const { fetch: fetchGetApplicationProposals } =
    useGetClientApplicationProposals();
  const { fetch: fetchPostApplication } = usePostClientApplication();
  const { fetch: fetchPutApplication } = usePutApplication();
  const ioBlackBox = useRecoilValue(ioBlackBoxAtom);
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const { sendSeonFraudInfo } = useSeon();
  const { generateJuicyApplicationSession } = useJuicy();
  const { sendAnalyticsCookiesIfExists } = useAnalyticsCookies();
  const { fetch: fetchGetApplication } = useGetClientApplication();

  useEffect(() => {
    injectJuicyScript();
    const getClient = async () => {
      await fetchClient();
    };

    if (!client) {
      getClient();
    }
  }, [client]);

  useEffect(() => {
    const getProposals = async () => {
      try {
        const proposals = await fetchGetApplicationProposals();

        const amount = proposals?.bestOfferProposal?.amount;
        const term = proposals?.bestOfferProposal?.term;

        if (amount && term) {
          await fetchOffer({
            pathParameters: {
              productNumber: client?.productNumber || App.config.productNumber,
            },
            queryParameters: {
              amount: amount,
              term: term,
            },
          });
        }
      } catch (e) {
        setError(e);
      }
    };
    getProposals();
  }, [client?.productNumber]);

  const OfferWithNoInterests = !Boolean(offerData?.totalInterest) || true;

  const handleConsentsChange = useCallback(() => {
    setConsents((value) => !value);
  }, []);

  const openLegalTermsModal = async (event: SyntheticEvent) => {
    try {
      event.preventDefault();
      setIsLoadingLegalTerms(true);

      const fetchedLegalTerms = await handleLegalTerms();
      setLegalTermsHtml(fetchedLegalTerms);
      setIsLegalTermsOpen(true);
    } catch {
    } finally {
      setIsLoadingLegalTerms(false);
    }
  };

  const handleLegalTerms = async () => {
    const amount = offerData?.totalPrincipal;
    const term = offerData?.term;

    try {
      const data = await apiLegalTerms.fetch({
        headers: getFetchHeaders({
          Accept: 'text/html',
          'Content-Type': 'text/html',
        }),
        queryParameters: {
          attachment: false,
          amount: String(amount),
          term: Number(term),
          productNumber: client?.productNumber || App.config.productNumber,
        },
      });
      return data as string;
    } catch (e) {
      setError(e);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);

      if (offerData?.totalPrincipal && offerData?.term) {
        const applicationCreated = await fetchPostApplication({
          body: {
            amount: offerData?.totalPrincipal,
            term: offerData?.term,
            productNumber: client?.productNumber,
            source: getApplicationSource(),
            ioBlackBox,
          },
        });
        postMessageToApp('application_first_main', {
          amount: offerData?.totalPrincipal,
          term: offerData?.term,
          productNumber: client?.productNumber,
          source: getApplicationSource(),
        });

        await sendAnalyticsCookiesIfExists();

        try {
          await sendSeonFraudInfo();
        } catch {}

        if (!applicationCreated?.rejected) {
          await fetchPutApplication({
            body: {
              ioBlackBox,
            },
          });
          await fetchGetApplication();
        }

        try {
          await generateJuicyApplicationSession({
            nextRoute: routes.FirstLoan_OnlineBank_Check,
          });
        } catch {}
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoadingSubmit(false);
      navigate(routes.FirstLoan_OnlineBank_Check); // Same screen as we have on tink redirection
    }
  };

  const closeLegalTermsModal = useCallback(() => {
    setIsLegalTermsOpen(false);
    setLegalTermsHtml(undefined);
  }, []);

  const term =
    client?.productNumber !== '7'
      ? i18Next.t('common:days')
      : i18Next.t('common:months');

  const content = getScreenTranslation('bestOffer', i18Next);

  return (
    <Frame
      {...frameConfig}
      sectionID={AppRegistrationSection.Summary}
      hideEditButton={true}
      bodyWrapperClassName={classes(
        layout['frame-x-compact'],
        layout['width-lg'],
      )}
    >
      <div className={styles.layout}>
        <div className={styles.title} data-test="title">
          {content.title}
        </div>
        <div className={styles.description} data-test="description">
          {content.description}
        </div>
        <Card className={styles.card}>
          <div
            className={classes([
              styles['card-header'],
              styles['amount-no-interests'],
            ])}
          >
            <div className={styles['amount-wrapper']}>
              <Text
                className={classes(styles.amount, styles['green-color'])}
                dataTest="amount-principal"
                text={
                  offerData?.totalPrincipal !== undefined
                    ? String(offerData?.totalPrincipal)
                    : undefined
                }
                weight="medium"
              />
              <Text
                className={classes(styles.currency, styles['green-color'])}
                dataTest="amount-currency"
                text={content.currency}
                weight="medium"
              />
            </div>
          </div>
          <div className={styles.separator}></div>
          <div className={styles['card-body']}>
            <div className={styles['card-body-layout']}>
              <div
                className={styles['table-title']}
                data-test="bestOffer-card-table-title"
              >
                {content.card.table.title}
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
              <div className={styles.row}>
                <div data-test="bestOffer-card-table-row1-text">
                  {content.card.table.row1}
                </div>
                <div data-test="bestOffer-card-table-row1-value">
                  <span className={styles['table-term-value']}>
                    {offerData?.term}
                  </span>
                  <span>{i18Next.t('card.table.row1TermValue', { term })}</span>
                </div>
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
              <div className={styles.row} data-test="bestOffer-card-table-row2">
                <div data-test="bestOffer-card-table-row2-text">
                  {content.card.table.row2}
                </div>
                <span data-test="bestOffer-card-table-row2-value">
                  {numberToCurrency(offerData?.totalPrincipal)}
                </span>
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
            </div>
            <div
              className={classes([
                styles.row,
                styles.row3,
                OfferWithNoInterests && styles['row-no-interests'],
              ])}
              data-test="bestOffer-card-table-row3"
            >
              <div data-test="bestOffer-card-table-row3-text">
                {content.card.table.row3}
              </div>
              <span data-test="bestOffer-card-table-row3-value">
                {numberToCurrency(offerData?.totalInterest)}
              </span>
            </div>
            <div
              className={classes([
                styles.separator,
                styles['no-mobile'],
                styles.row3,
              ])}
            ></div>
            <div className={styles['card-body-layout']}>
              <div className={styles.row} data-test="bestOffer-card-table-row4">
                <div data-test="bestOffer-card-table-row4-text">
                  {content.card.table.row4}
                </div>
                <span data-test="bestOffer-card-table-row4-value">
                  {numberToCurrency(offerData?.totalRepayableAmount)}
                </span>
              </div>
              <div
                className={classes([styles.separator, styles['no-mobile']])}
              ></div>
              <div className={styles.row} data-test="bestOffer-card-table-row5">
                <div data-test="bestOffer-card-table-row5-text">
                  {content.card.table.row5}
                </div>
                <span data-test="bestOffer-card-table-row5-value">
                  {dateFormat(offerData?.agreementEndDate)}
                </span>
              </div>
            </div>
          </div>
        </Card>
        <div className={styles['checkbox-wrapper']}>
          <Checkbox
            dataTest="consents-checkbox"
            name="consents"
            onChange={handleConsentsChange}
            value={consents}
          />
          <div className={styles['checkbox-content']}>
            <Text
              dangerouslySetInnerHTML={{
                __html: content.consents,
              }}
              dataTest="consents"
            />
            <Link
              className={styles['legal-terms-link']}
              dataTest="terms-link"
              disabled={isLoadingLegalTerms}
              href="#"
              onClick={openLegalTermsModal}
            >
              {content.consents_link}
            </Link>
          </div>
        </div>
        <div className={styles['button-wrapper']}>
          <Button
            className={styles.button}
            disabled={!offerData || !consents}
            onClick={handleSubmit}
            loading={!offerData || loadingSubmit}
          >
            {content.submitBtn}
          </Button>
        </div>
        <div className={styles.footer}>
          <div className={styles['footer-title']} data-test="footer-title">
            {content.footer.title}
          </div>
          <div
            className={styles['footer-body']}
            dangerouslySetInnerHTML={{
              __html: content.footer.description,
            }}
            data-test="footer-description"
          ></div>
        </div>
        {isLegalTermsOpen && (
          <Modal
            className={styles.modal}
            isOpen={isLegalTermsOpen}
            onClose={closeLegalTermsModal}
          >
            <div
              dangerouslySetInnerHTML={{ __html: legalTermsHtml || '' }}
              data-test="footer-legal-terms"
            ></div>
          </Modal>
        )}
      </div>
    </Frame>
  );
}
